import type PubNub from 'pubnub';

import type { BidPlacedEvent, LiveAuctionEvent } from '@/types/LiveAuctionEvent';
import type { PubSubProvider } from '@/types/PubSubProvider';

import { getBidderId } from '@/redux/modules/account/user/user.selectors';
import { fetchBidLimitBalanceIfNeeded } from '@/redux/modules/bidLimit/bidLimit.actions';
import { getBidLimitBalance } from '@/redux/modules/bidLimit/bidLimit.selectors';
import { fetchLiveCatalogRegistrationByIdIfNeeded } from '@/redux/modules/catalog/registration/catalogRegistration.actions';
import { getPubSubMessagingProvider } from '@/redux/modules/config';
import { getCatalogNetworkStatus, getLiveItemDataLeadingBid } from '@/redux/modules/console';
import {
    getItemIdsForCatalog,
    getItemSummariesForCatalog,
    getItemSummaryIsTimedPlusAuction,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { AppDispatch, AppGetState } from '@/redux/store';

import {
    LIVE_ASK_CHANGED,
    LIVE_ASK_CHANGED_ACTION,
    LIVE_AUCTION_ENDED,
    LIVE_AUCTION_ENDED_ACTION,
    LIVE_AUCTION_LOADED,
    LIVE_AUCTION_LOADED_ACTION,
    LIVE_AUCTION_MOVED,
    LIVE_AUCTION_MOVED_ACTION,
    LIVE_AUCTION_OPENED,
    LIVE_AUCTION_OPENED_ACTION,
    LIVE_AUCTION_PAUSED,
    LIVE_AUCTION_PAUSED_ACTION,
    LIVE_AUCTION_RESUMED,
    LIVE_AUCTION_RESUMED_ACTION,
    LIVE_AUCTION_STARTED,
    LIVE_AUCTION_STARTED_ACTION,
    LIVE_BID_ACCEPTED,
    LIVE_BID_PLACED,
    LIVE_BID_RETRACTED,
    LIVE_LOT_CLOSED,
    LIVE_LOT_CLOSED_ACTION,
    LIVE_LOT_PASSED,
    LIVE_LOT_PASSED_ACTION,
    LIVE_LOT_REOPENED,
    LIVE_LOT_REOPENED_ACTION,
    LIVE_LOT_SKIPPED,
    LIVE_LOT_SKIPPED_ACTION,
    LIVE_LOT_SOLD,
    LIVE_LOT_SOLD_ACTION,
    LIVE_LOT_UNSOLD,
    LIVE_LOT_UNSOLD_ACTION,
    LIVE_MISSIVE,
    LIVE_MISSIVE_ACTION,
    LIVE_NETWORK_DISCONNECTED,
    LIVE_NETWORK_DISCONNECTED_ACTION,
    LIVE_NETWORK_RECONNECTED,
    LIVE_NETWORK_RECONNECTED_ACTION,
    LIVE_NEXT_LOT_LOADED,
    LIVE_NEXT_LOT_LOADED_ACTION,
    LIVE_UPDATE_CATALOG_OCCUPANCY,
    LIVE_UPDATE_CATALOG_OCCUPANCY_ACTION,
    LIVE_UPDATE_TIMESTAMP,
    LIVE_UPDATE_TIMESTAMP_ACTION,
    LiveBidAcceptedAction,
    LiveBidPlacedAction,
    LiveBidRetractedAction,
} from './actions';
import { getCatalogCurrency, getCatalogIsTimed, getCatalogSellerId } from './catalog/catalogs/catalog.selectors';
import { fetchItemSummariesIfNeeded } from './item/summary/itemSummary.actions';
import { getUiLiveBid } from './liveBid';
import { fetchLiveCatalogStatusIfNeeded } from './liveCatalogStatus';

/* ACTION CREATORS */
export const handleCatalogOnMessage =
    (originalCatalogId: number) =>
    (event: LiveAuctionEvent) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        if ('AuctionId' in event && event.AuctionId === originalCatalogId) {
            const { AuctionId: catalogId, Created: serverCreatedTime } = event;
            const updateTimestampAction: LIVE_UPDATE_TIMESTAMP_ACTION = {
                error: false,
                payload: {
                    catalogId,
                    timestamp: Date.now(),
                },
                type: LIVE_UPDATE_TIMESTAMP,
            };

            dispatch(updateTimestampAction);

            const currentTime = Date.now();

            const state = getState();
            const currentBidderId = getBidderId(state);
            const currency = getCatalogCurrency(state, catalogId);
            const sellerId = getCatalogSellerId(state, catalogId);
            const isTimed = getCatalogIsTimed(state, catalogId);

            if (event.EventType === 'ask-changed') {
                const { AskPrice, LotId, LotIndex, LotNumber } = event;
                return dispatch({
                    payload: {
                        ask: AskPrice,
                        catalogId,
                        created: currentTime,
                        currency,
                        itemId: LotId,
                        itemIndex: LotIndex,
                        lotNumber: LotNumber,
                    },
                    type: LIVE_ASK_CHANGED,
                } as LIVE_ASK_CHANGED_ACTION);
            } else if (event.EventType === 'auction-closed') {
                const liveAuctionEndedAction: LIVE_AUCTION_ENDED_ACTION = {
                    error: false,
                    payload: { catalogId, created: currentTime },
                    type: LIVE_AUCTION_ENDED,
                };
                return dispatch(liveAuctionEndedAction);
            } else if (event.EventType === 'auction-loaded') {
                const { ServerId } = event;
                await dispatch(
                    fetchLiveCatalogRegistrationByIdIfNeeded({
                        catalogId,
                        sellerId,
                    })
                );

                return dispatch({
                    payload: {
                        catalogId,
                        created: currentTime,
                        serverId: ServerId,
                    },
                    type: LIVE_AUCTION_LOADED,
                } as LIVE_AUCTION_LOADED_ACTION);
            } else if (event.EventType === 'auction-moved') {
                return dispatch({
                    payload: { catalogId, created: currentTime },
                    type: LIVE_AUCTION_MOVED,
                } as LIVE_AUCTION_MOVED_ACTION);
            } else if (event.EventType === 'auction-opened') {
                const { ServerId } = event;
                await dispatch(
                    fetchLiveCatalogRegistrationByIdIfNeeded({
                        catalogId,
                        sellerId,
                    })
                );

                return dispatch({
                    payload: {
                        catalogId,
                        created: currentTime,
                        serverId: ServerId,
                    },
                    type: LIVE_AUCTION_OPENED,
                } as LIVE_AUCTION_OPENED_ACTION);
            } else if (event.EventType === 'auction-paused') {
                return dispatch({
                    payload: {
                        catalogId,
                        created: currentTime,
                        serverCreatedTime,
                    },
                    type: LIVE_AUCTION_PAUSED,
                } as LIVE_AUCTION_PAUSED_ACTION);
            } else if (event.EventType === 'auction-resumed') {
                return dispatch({
                    payload: {
                        catalogId,
                        created: currentTime,
                        serverCreatedTime,
                    },
                    type: LIVE_AUCTION_RESUMED,
                } as LIVE_AUCTION_RESUMED_ACTION);
            } else if (event.EventType === 'auction-started') {
                return dispatch({
                    payload: { catalogId, created: currentTime },
                    type: LIVE_AUCTION_STARTED,
                } as LIVE_AUCTION_STARTED_ACTION);
            } else if (event.EventType === 'bid-accepted') {
                const {
                    Amount,
                    AskPrice,
                    AssignedId,
                    BidCount,
                    BidderId,
                    BidSource,
                    EndTime,
                    IsExtended,
                    IsReserveMet,
                    LotId,
                    LotIndex,
                    LotNumber,
                } = event;
                return dispatch(
                    bidAccepted({
                        amount: Amount,
                        ask: AskPrice,
                        bidCount: BidCount,
                        bidderId: BidderId,
                        catalogId,
                        created: currentTime,
                        currency,
                        endTime: EndTime,
                        isExtended: IsExtended,
                        isReserveMet: IsReserveMet,
                        itemId: LotId,
                        itemIndex: LotIndex,
                        lotNumber: LotNumber,
                        myBid: BidderId === currentBidderId && BidderId > 0,
                        paddleNumber: AssignedId,
                        serverCreatedTime,
                        source: BidSource,
                    })
                );
            } else if (event.EventType === 'bid-placed') {
                return dispatch(bidPlaced(event));
            } else if (event.EventType === 'bid-retracted' || event.EventType === 'bidder-retraction') {
                const {
                    Amount,
                    AskPrice,
                    BidCount,
                    BidderId,
                    BidSource,
                    EndTime,
                    IsExtended,
                    IsReserveMet,
                    LotId,
                    LotIndex,
                    LotNumber,
                } = event;
                return dispatch(
                    bidRetracted({
                        amount: Amount,
                        ask: AskPrice,
                        bidCount: BidCount,
                        bidderId: BidderId,
                        catalogId,
                        created: currentTime,
                        currency,
                        endTime: EndTime,
                        isExtended: IsExtended,
                        isReserveMet: IsReserveMet,
                        itemId: LotId,
                        itemIndex: LotIndex,
                        lotNumber: LotNumber,
                        serverCreatedTime,
                        source: BidSource,
                    })
                );
            } else if (event.EventType === 'lot-closed') {
                const { LotId, LotNumber } = event;
                return dispatch({
                    payload: {
                        catalogId,
                        created: currentTime,
                        itemId: LotId,
                        lotNumber: LotNumber,
                    },
                    type: LIVE_LOT_CLOSED,
                } as LIVE_LOT_CLOSED_ACTION);
            } else if (event.EventType === 'lot-loaded') {
                const { NextLot } = event;
                return dispatch({
                    payload: {
                        askPrice: NextLot.AskPrice,
                        catalogId,
                        created: currentTime,
                        currency,
                        description: NextLot.LotDescription,
                        highEstimate: NextLot.HighEstimate,
                        index: NextLot.Index,
                        itemId: NextLot.LotId,
                        itemIndex: NextLot.Index,
                        lotNumber: NextLot.LotNumber,
                        lotTitle: NextLot.LotTitle,
                        lowEstimate: NextLot.LowEstimate,
                        status: NextLot.Status,
                    },
                    type: LIVE_NEXT_LOT_LOADED,
                } as LIVE_NEXT_LOT_LOADED_ACTION);
            } else if (event.EventType === 'lot-passed') {
                const { LotId, LotNumber, LotsClosed } = event;
                const action: LIVE_LOT_PASSED_ACTION = {
                    error: false,
                    payload: {
                        catalogId,
                        closedLotsCount: LotsClosed,
                        created: currentTime,
                        itemId: LotId,
                        lotNumber: LotNumber,
                        serverCreatedTime,
                    },
                    type: LIVE_LOT_PASSED,
                };
                return dispatch(action);
            } else if (event.EventType === 'lot-reopened') {
                const { AskPrice, HighBid, Increment, LeadingBidderId, LotId, LotIndex, LotNumber } = event;
                return dispatch({
                    payload: {
                        askPrice: AskPrice,
                        catalogId,
                        created: currentTime,
                        increment: Increment,
                        itemId: LotId,
                        itemIndex: LotIndex,
                        leadingBid: HighBid,
                        leadingBidder: LeadingBidderId,
                        lotNumber: LotNumber,
                        myBid: LeadingBidderId === currentBidderId && LeadingBidderId > 0,
                    },
                    type: LIVE_LOT_REOPENED,
                } as LIVE_LOT_REOPENED_ACTION);
            } else if (event.EventType === 'lot-skipped') {
                const { LotId, LotNumber } = event;
                return dispatch({
                    payload: {
                        catalogId,
                        created: currentTime,
                        itemId: LotId,
                        lotNumber: LotNumber,
                    },
                    type: LIVE_LOT_SKIPPED,
                } as LIVE_LOT_SKIPPED_ACTION);
            } else if (event.EventType === 'lot-sold') {
                const { Amount, AssignedId, BidderId, LotId, LotNumber, LotsClosed } = event;
                const myBid = BidderId === currentBidderId && BidderId > 0;

                dispatch(
                    fetchBidLimitBalanceIfNeeded({
                        catalogId,
                        force: true,
                        itemId: LotId,
                    })
                );

                const action: LIVE_LOT_SOLD_ACTION = {
                    error: false,
                    payload: {
                        amount: Amount,
                        bidderId: BidderId,
                        catalogId,
                        closedLotsCount: LotsClosed,
                        created: currentTime,
                        currency,
                        itemId: LotId,
                        lotNumber: LotNumber,
                        myBid,
                        paddleNumber: `${AssignedId}`,
                        serverCreatedTime,
                    },
                    type: LIVE_LOT_SOLD,
                };

                return dispatch(action);
            } else if (event.EventType === 'lot-unsold') {
                const { LotId, LotNumber } = event;
                return dispatch({
                    payload: {
                        catalogId,
                        created: currentTime,
                        itemId: LotId,
                        lotNumber: LotNumber,
                    },
                    type: LIVE_LOT_UNSOLD,
                } as LIVE_LOT_UNSOLD_ACTION);
            } else if (event.EventType === 'missive-sent') {
                const { LotId, MissiveText } = event;

                return dispatch({
                    payload: {
                        catalogId,
                        created: currentTime,
                        isTimed,
                        itemId: LotId,
                        missiveText: MissiveText,
                    },
                    type: LIVE_MISSIVE,
                } as LIVE_MISSIVE_ACTION);
            }
        }
    };

type HandleCatalogOnPresenceParams = {
    channel: string;
    occupancy: number;
};

/**
 * Update the presence count in redux if pubnub receives 'enter' or 'leave' presence messages
 */
export const handleCatalogOnPresence =
    (catalogId: number) => (presenceEvent: HandleCatalogOnPresenceParams) => async (dispatch: AppDispatch) => {
        if (presenceEvent.channel.includes(`${catalogId}`)) {
            const action: LIVE_UPDATE_CATALOG_OCCUPANCY_ACTION = {
                error: false,
                payload: { bidderCount: presenceEvent.occupancy, catalogId },
                type: LIVE_UPDATE_CATALOG_OCCUPANCY,
            };
            return dispatch(action);
        }
    };

/**
 * Handle PubNub client connection errors
 * @see https://www.pubnub.com/docs/connections/connection-management#connection-issues
 */
export const handlePubnubCatalogOnStatus =
    (catalogId: number) =>
    (statusEvent: PubNub.Status | PubNub.StatusEvent) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        const state = getState();
        const currentProvider = getPubSubMessagingProvider(state);
        const catalogNetworkStatus = getCatalogNetworkStatus(state, catalogId);

        if (currentProvider !== 'pubnub') {
            return;
        }

        // PubNub connection re-established
        if (statusEvent.category === 'PNNetworkUpCategory' && catalogNetworkStatus === 'disconnected') {
            return dispatch(networkReconnected({ catalogId, provider: 'pubnub' }));
            // PubNub connection dropped
        } else if (statusEvent.category === 'PNNetworkDownCategory' && catalogNetworkStatus === 'connected') {
            return dispatch(networkDisconnected({ catalogId, provider: 'pubnub' }));
        } else if (statusEvent.category === 'PNNetworkIssuesCategory' && catalogNetworkStatus === 'connected') {
            return dispatch(networkDisconnected({ catalogId, provider: 'pubnub' }));
        } else if (statusEvent.category === 'PNReconnectedCategory' && catalogNetworkStatus === 'disconnected') {
            return dispatch(networkReconnected({ catalogId, provider: 'pubnub' }));
        }

        return Promise.resolve();
    };

type NetworkStatusParams = {
    catalogId: number;
    provider: PubSubProvider;
};

const networkDisconnected = ({ catalogId, provider }: NetworkStatusParams) => {
    const action: LIVE_NETWORK_DISCONNECTED_ACTION = {
        error: false,
        payload: { catalogId, provider },
        type: LIVE_NETWORK_DISCONNECTED,
    };
    return action;
};

export const networkReconnected =
    ({ catalogId, provider }: NetworkStatusParams) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        const state = getState();
        const firstCatalogItem = getItemSummariesForCatalog(state, catalogId)[0];
        const itemIds = getItemIdsForCatalog(state, catalogId);

        // Update item data when pub/sub reconnects since client has likely missed some LiveAuctionEvent messages
        await dispatch(
            fetchItemSummariesIfNeeded({
                force: true,
                identifier: 'reconnecting-pubsub',
                itemIds,
            })
        );
        await dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, firstCatalogItem?.sellerId, true));

        const action: LIVE_NETWORK_RECONNECTED_ACTION = {
            error: false,
            payload: { catalogId, provider },
            type: LIVE_NETWORK_RECONNECTED,
        };
        return dispatch(action);
    };

const bidAccepted =
    ({
        amount,
        ask,
        bidCount,
        bidderId,
        catalogId,
        created,
        currency,
        endTime,
        isExtended,
        isReserveMet,
        itemId,
        itemIndex,
        lotNumber,
        myBid,
        paddleNumber,
        serverCreatedTime,
        source,
    }) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        const state = getState();
        const currentBidderId = getBidderId(state);
        const { bidderHasHighBid, submitted } = getUiLiveBid(state);
        const bidderOutbid =
            (currentBidderId !== bidderId && bidderHasHighBid && bidderId > 0) || (submitted && !myBid);
        const bidLimitBalance = getBidLimitBalance(state, catalogId);
        const previousBidAmount = getLiveItemDataLeadingBid(state, itemId);
        const isTimedPlusAuction = getItemSummaryIsTimedPlusAuction(state, itemId);

        dispatch(fetchBidLimitBalanceIfNeeded({ catalogId, force: true, itemId }));

        const acceptedAction: LiveBidAcceptedAction = {
            error: false,
            payload: {
                amount,
                ask,
                bidCount,
                bidderId,
                bidderOutbid,
                bidLimitBalance,
                catalogId,
                created,
                currency,
                endTime,
                isCurrentBidder: currentBidderId === bidderId,
                isExtended,
                isReserveMet,
                isTimedPlusAuction,
                itemId,
                itemIndex,
                lotNumber,
                myBid,
                paddleNumber,
                previousBidAmount,
                serverCreatedTime,
                source,
            },
            type: LIVE_BID_ACCEPTED,
        };

        return dispatch(acceptedAction);
    };

const bidPlaced = (event: BidPlacedEvent) => async (dispatch: AppDispatch, getState: AppGetState) => {
    const state = getState();
    const currentBidderId = getBidderId(state);
    const currentUserPlacedBid = event.BidderId === currentBidderId;

    const bidPlacedAction: LiveBidPlacedAction = {
        error: false,
        payload: {
            amount: event.Amount,
            bidderId: event.BidderId,
            bidSource: event.BidSource,
            catalogId: event.AuctionId,
            created: event.Created,
            currentUserPlacedBid,
            lotId: event.LotId,
        },
        type: LIVE_BID_PLACED,
    };
    return dispatch(bidPlacedAction);
};

const bidRetracted =
    ({
        amount,
        ask,
        bidCount,
        bidderId,
        catalogId,
        created,
        currency,
        endTime,
        isExtended,
        isReserveMet,
        itemId,
        itemIndex,
        lotNumber,
        serverCreatedTime,
        source,
    }) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        const state = getState();
        const currentBidderId = getBidderId(state);
        const myBid = bidderId === currentBidderId;
        const isTimedPlusAuction = getItemSummaryIsTimedPlusAuction(state, itemId);

        dispatch(fetchBidLimitBalanceIfNeeded({ catalogId, force: true, itemId }));

        const bidRetractedAction: LiveBidRetractedAction = {
            error: false,
            payload: {
                amount,
                ask,
                bidCount,
                bidderId,
                catalogId,
                created,
                currency,
                currentBidderId,
                endTime,
                isExtended,
                isReserveMet,
                isTimedPlusAuction,
                itemId,
                itemIndex,
                lotNumber,
                myBid,
                serverCreatedTime,
                source,
            },
            type: LIVE_BID_RETRACTED,
        };

        return dispatch(bidRetractedAction);
    };
